import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
	ColumnDef,
	PaginationState,
	SortingState,
} from '@tanstack/react-table';
import { Link } from 'react-router-dom';

import GridTable from '../../../components/GridTable';
import { RecruiterInformationModel } from '../../../models/recruiterInformation.model';
import { GridPayload } from '../../../models/gridPayload.model';
import { route } from '../../../constants';
import { Stage } from '../../../enums/enum';
import { getAllRecruits } from '../../../services/recruitsService';
import { getFormattedDateString } from '../../../functions/dateFunction';
import {
	formatStringToPascal,
	getCurrencyFormat,
	getStringFromPascal,
} from '../../../functions/stringFunctions';
import { setlistPageAndSort } from '../../../store/reducers/listPageAndSortReducer';
import { toastHandler } from '../../../utils/toastHandler';
import { RootState } from '../../../store/store';
import { phoneNumberWithCountryCode } from '../../../utils/phone-format';

const MyRecruits = () => {
	const [totalCount, setTotalCount] = useState(0);
	const pageSortData = useSelector(
		(state: RootState) => state.listPageAndSort,
	);
	const dispatch = useDispatch();
	const [pagination, setPagination] = useState<PaginationState>({
		pageIndex: pageSortData?.page?.page_number
			? pageSortData?.page?.page_number
			: 0,
		pageSize: pageSortData?.page?.page_size
			? pageSortData?.page?.page_size
			: 10,
	});
	const search = useSelector((state: RootState) => state.search);
	const userEmail = useSelector((state: RootState) => state.user.user_email);
	const filters = useSelector((state: RootState) => state.listFilter);
	const [sort, setSort] = useState<SortingState>([
		{
			desc: !pageSortData?.sort?.sort_ascending || false,
			id: getStringFromPascal(pageSortData.sort.sort_column),
		},
	]);

	const [rows, setRows] = useState<RecruiterInformationModel[]>([]);

	const handleGetAllRecruits = async (payload: GridPayload) => {
		try {
			const data = await getAllRecruits(payload);
			if (!data?.value) return;
			const totalCount = data.value.total_count || 0;
			const recruits = [...data.value.recruits_information];
			setTotalCount(totalCount);
			setRows(recruits);
			dispatch(
				setlistPageAndSort({
					...payload.page_filter_search,
					page: {
						page_number:
							payload.page_filter_search.page.page_number - 1,
						page_size: payload.page_filter_search.page.page_size,
					},
					sort: {
						sort_column:
							payload.page_filter_search.sort.sort_column,
						sort_ascending:
							payload.page_filter_search.sort.sort_ascending,
					},
				}),
			);
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
		} catch (e: any) {
			console.error(e);
			toastHandler({
				isError: true,
				msg: 'Failed to update the changes',
			});
		}
	};

	useEffect(() => {
		if (
			(filters?.roles ||
				filters?.stages?.length ||
				filters?.all_external_managers?.length ||
				filters?.all_companies?.length ||
				filters?.states?.length) &&
			filters.isFilterRequired
		) {
			const handle = async () => {
				const updatedPayload = {
					user_email: userEmail || '',
					page_filter_search: {
						page: {
							page_size: pagination.pageSize,
							page_number: 1,
						},
						search: search,
						sort: {
							sort_column: formatStringToPascal(
								sort?.[0]?.id || '',
							),
							sort_ascending: !sort?.[0]?.desc,
						},
						filter: {
							role_type_id: filters?.roles || null,
							stage_type_ids: filters?.stages || [],
							external_manager_ids:
								filters?.all_external_managers || [],
							company_ids: filters?.all_companies || [],
							state_ids: filters?.states || [],
						},
					},
				};
				await handleGetAllRecruits(updatedPayload);
				setPagination({ pageIndex: 0, pageSize: pagination.pageSize });
			};
			handle();
		}

		if (
			filters?.roles === null &&
			filters?.stages?.length === 0 &&
			filters?.all_external_managers?.length === 0 &&
			filters?.all_companies?.length === 0 &&
			filters?.states?.length === 0
		) {
			const handle = async () => {
				const updatedPayload = {
					user_email: userEmail || '',
					page_filter_search: {
						page: {
							page_size: pagination.pageSize,
							page_number: pagination.pageIndex + 1,
						},
						search: search,
						sort: {
							sort_column: formatStringToPascal(
								sort?.[0]?.id || '',
							),
							sort_ascending: !sort?.[0]?.desc,
						},
						filter: {
							role_type_id: filters?.roles || null,
							stage_type_ids: filters?.stages || [],
							external_manager_ids:
								filters?.all_external_managers || [],
							company_ids: filters?.all_companies || [],
							state_ids: filters?.states || [],
						},
					},
				};
				await handleGetAllRecruits(updatedPayload);
			};
			handle();
		}
	}, [filters]);

	useEffect(() => {
		const payload = {
			user_email: userEmail || '',
			page_filter_search: {
				page: {
					page_size: pagination.pageSize,
					page_number: pagination.pageIndex + 1,
				},
				search: search,
				sort: {
					sort_column: formatStringToPascal(sort?.[0]?.id || ''),
					sort_ascending: !sort?.[0]?.desc,
				},
				filter: {
					role_type_id: filters?.roles || null,
					stage_type_ids: filters?.stages || [],
					external_manager_ids: filters?.all_external_managers || [],
					company_ids: filters?.all_companies || [],
					state_ids: filters?.states || [],
				},
			},
		};
		handleGetAllRecruits(payload);
	}, [search]);

	const getStage = (val: string) => {
		switch (val) {
			case Stage.offer:
				return (
					<div className="flex gap-x-1 text-rcrm-lime items-center">
						<span className="bg-rcrm-lime w-2 h-2 rounded-xl"></span>
						<p>{val}</p>
					</div>
				);
			case Stage.prospects:
				return (
					<div className="flex gap-x-1 text-rcrm-secondary-blue items-center">
						<span className="bg-rcrm-secondary-blue w-2 h-2 rounded-xl"></span>
						<p>{val}</p>
					</div>
				);
			case Stage.lead:
				return (
					<div className="flex gap-x-1 text-rcrm-primary-blue items-center">
						<span className="bg-rcrm-primary-blue w-2 h-2 rounded-xl"></span>
						<p>{val}</p>
					</div>
				);
			case Stage.dueDiligence:
				return (
					<div className="flex gap-x-1 text-rcrm-blue items-center">
						<span className="bg-rcrm-blue w-2 h-2 rounded-xl"></span>
						<p>{val}</p>
					</div>
				);
			case Stage.siteVisit:
				return (
					<div className="flex gap-x-1 text-rcrm-bluish-green items-center">
						<span className="bg-rcrm-bluish-green w-2 h-2 rounded-xl"></span>
						<p>{val}</p>
					</div>
				);
			default:
				return 'NA';
		}
	};

	const columns: ColumnDef<RecruiterInformationModel>[] = [
		{
			accessorKey: 'recruit_name',
			cell: (info) => (
				<Link
					className="text-rcrm-primary-blue underline w-[200px]"
					to={`${process.env.PUBLIC_URL}/${route.recruitInformation}/${route.details}?id=${info.row.original.recruit_id}`}
				>
					{(info.getValue() as string) || 'NA'}
				</Link>
			),
			header: () => <span className="w-[200px]">Recruit Name</span>,
			footer: (props) => props.column.id,
			enableSorting: true,
		},
		{
			accessorKey: 'role',
			cell: (info) => (
				<span className="w-[120px]">
					{(info.getValue() as string) || 'NA'}
				</span>
			),
			header: () => <span className="w-[120px]">Role</span>,
			footer: (props) => props.column.id,
			enableSorting: false,
		},
		{
			accessorKey: 'first_contact_date',
			cell: (info) => (
				<span className="w-[180px]">
					{getFormattedDateString(info.getValue() as string) || 'NA'}
				</span>
			),
			header: () => <span className="w-[180px]">First Contact Date</span>,
			footer: (props) => props.column.id,
			enableSorting: true,
		},
		{
			accessorKey: 'last_contact_date',
			cell: (info) => (
				<span className="w-[160px]">
					{getFormattedDateString(info.getValue() as string) || 'NA'}
				</span>
			),
			header: () => <span className="w-[160px]">Last Activity Date</span>,
			footer: (props) => props.column.id,
			enableSorting: true,
		},
		{
			accessorKey: 'stage',
			cell: (info) => (
				<span className="w-[140px]">
					{getStage((info.getValue() as string) || 'NA')}
				</span>
			),
			header: () => <span className="w-[140px]">Stage</span>,
			footer: (props) => props.column.id,
			enableSorting: false,
		},
		{
			accessorKey: 'email',
			cell: (info) => (
				<span className="overflow-hidden whitespace-nowrap text-ellipsis w-[250px]">
					{(info.getValue() as string) || 'NA'}
				</span>
			),
			header: () => <span className="w-[250px]">Email</span>,
			footer: (props) => props.column.id,
			enableSorting: false,
		},
		{
			accessorKey: 'phone',
			cell: (info) => (
				<span className="w-[200px]">
					{phoneNumberWithCountryCode(info.getValue() as string) ||
						'NA'}
				</span>
			),
			header: () => <span className="w-[200px]">Phone</span>,
			footer: (props) => props.column.id,
			enableSorting: false,
		},
		{
			accessorKey: 'state',
			cell: (info) => (
				<span className="w-[100px]">
					{(info.getValue() as string) || 'NA'}
				</span>
			),
			header: () => <span className="w-[100px]">State</span>,
			footer: (props) => props.column.id,
			enableSorting: true,
			sortDescFirst: false,
		},
		{
			accessorKey: 'total_staff',
			cell: (info) => (
				<span className="w-[100px]">
					{(info.getValue() as string) || 'NA'}
				</span>
			),
			header: () => <span className="w-[100px]">Total Staff</span>,
			footer: (props) => props.column.id,
			enableSorting: false,
		},
		{
			accessorKey: 'outside_recruiter',
			cell: (info) => (
				<span className="w-[200px]">
					{(info.getValue() as string) || 'NA'}
				</span>
			),
			header: () => <span className="w-[200px]">Outsider Recruiter</span>,
			footer: (props) => props.column.id,
			enableSorting: false,
		},
		{
			accessorKey: 'company_name',
			cell: (info) => (
				<span className="w-[160px]">
					{(info.getValue() as string) || 'NA'}
				</span>
			),
			header: () => <span className="w-[160px]">Current Company</span>,
			footer: (props) => props.column.id,
			enableSorting: false,
		},
		{
			accessorKey: 'production',
			cell: (info) => (
				<span className="w-[180px]">
					{info.getValue() !== null || info.getValue() !== undefined
						? getCurrencyFormat(info.getValue() as number)
						: 'NA'}
				</span>
			),
			header: () => <span className="w-[180px]">Monthly Volume</span>,
			footer: (props) => props.column.id,
			enableSorting: true,
			sortDescFirst: false,
		},
	];

	const handleSorting = (sorting: SortingState) => {
		setSort(sorting);
		if (!sorting?.length && sort?.[0].id === 'recruit_name') return;
		const isAsc = !sorting?.[0]?.desc || false;
		const updatedPayload = {
			user_email: userEmail || '',
			page_filter_search: {
				page: {
					page_size: pagination.pageSize,
					page_number: pagination.pageIndex + 1,
				},
				search: search,
				sort: {
					sort_column: formatStringToPascal(sorting?.[0]?.id || ''),
					sort_ascending: isAsc,
				},
				filter: {
					role_type_id: filters?.roles || null,
					stage_type_ids: filters?.stages || [],
					external_manager_ids: filters?.all_external_managers || [],
					company_ids: filters?.all_companies || [],
					state_ids: filters?.states || [],
				},
			},
		};
		handleGetAllRecruits(updatedPayload);
	};

	const handlePagination = (updatedPagination: PaginationState) => {
		const updatedPayload = {
			user_email: userEmail || '',
			page_filter_search: {
				page: {
					page_size: updatedPagination.pageSize,
					page_number: updatedPagination.pageIndex + 1,
				},
				search: search,
				sort: {
					sort_column: formatStringToPascal(sort?.[0]?.id || ''),
					sort_ascending: !sort?.[0]?.desc,
				},
				filter: {
					role_type_id: filters?.roles || null,
					stage_type_ids: filters?.stages || [],
					external_manager_ids: filters?.all_external_managers || [],
					company_ids: filters?.all_companies || [],
					state_ids: filters?.states || [],
				},
			},
		};
		setPagination(updatedPagination);
		handleGetAllRecruits(updatedPayload);
	};

	return (
		<GridTable
			columns={columns}
			pagination={pagination}
			sorting={sort}
			rowCount={totalCount}
			rows={rows}
			handlePagination={handlePagination}
			handleSorting={handleSorting}
			isHeaderSticky={true}
		/>
	);
};
export default MyRecruits;
